import React, {useEffect} from 'react';
import Referral from '../../components/referral/Referral';
import {ImageCheck, Speedometer, UploadCloud1} from 'src/ui-components/Iconpack';
import {OrganizationTier, ReferralCodeInterface} from 'src/reducers/users';
import CustomProgressBar from 'src/components/custom-progress-tooltip/CustomProgressTooltip';
import {RootState, useAppDispatch} from 'src/store';
import {useSelector} from 'react-redux';
import {getMetrics, TimeSpanType} from 'src/actions/metrics';
import {CommonSpinLoader} from '../../ui-components/loaders';
import {useNavigate} from 'react-router-dom';
import {TierOrPlan} from '../../utils/tier';
import {UpgradeCard} from '../upgrade/UpgradeCard';

interface UserPlanProps {
    referralCode: ReferralCodeInterface;
    referralCount: number;
    tier?: OrganizationTier;
}

const ProgressWidth = [
    {width: 142, style: 'w-[142px]'},
    {width: 132, style: 'w-[132px]'},
    {width: 122, style: 'w-[122px]'},
    {width: 112, style: 'w-[122px]'},
    {width: 102, style: 'w-[122px]'},
    {width: 92, style: 'w-[92px]'},
];

const ProgressSection: React.FC<{icon: React.ReactNode; progressBar: React.ReactNode; text: string}> = ({
    icon,
    progressBar,
    text,
}) => (
    <div className='flex items-center gap-[11px] pb-[1rem]'>
        {icon}
        {progressBar}
        <span className='text-[12px] font-[400] text-[#FFFFFF]'>{text}</span>
    </div>
);

const UserPlan: React.FC<UserPlanProps> = (props) => {
    const showReferral = props.referralCode.code !== '';
    const referralCount = props.referralCount;
    const dispatch = useAppDispatch();
    const GB = 1024 * 1024 * 1024;
    const MB = 1024 * 1024;
    const getReferredUser = () => (referralCount === 0 ? 0 : Math.round((referralCount / 10) * 10000) / 100);
    const [metrics, userProfileProps] = [
        useSelector((state: RootState) => state.metrics),
        useSelector((state: RootState) => state.userProfile),
    ];
    const navigate = useNavigate();
    const tier = props.tier?.type ?? TierOrPlan.Free;
    const tierText =
        tier === TierOrPlan.Free
            ? 'Starter'
            : tier === TierOrPlan.Pro
              ? 'Pro'
              : tier === TierOrPlan.Hobbyist
                ? 'Hobbyist'
                : tier === TierOrPlan.PixelClassic
                  ? 'PixelClassic'
                  : tier === TierOrPlan.PixelStudio
                    ? 'PixelStudio'
                    : 'Custom';

    useEffect(() => {
        const org_uid = userProfileProps.currentOrganization.uid;
        if (org_uid) {
            dispatch(getMetrics({org_uid, timespan_type: TimeSpanType.LastOneMonth}));
        }
    }, [userProfileProps.currentOrganization]);

    let StorageText = '0/0 GB',
        BandwidthText = '0/0 GB';
    let storagePercentage = 0;
    let bandwidthPercentage = 0;
    let nudgeForPlanUpgrade = false;

    function formatUsageMetric(usedBytes: number, limitBytes: number): string {
        let numericLimit = limitBytes >= GB ? limitBytes / GB : limitBytes / MB;
        return limitBytes >= GB
            ? `${(usedBytes / GB).toFixed(1)}/${numericLimit.toFixed(numericLimit < 10 ? 1 : 0)} GB`
            : `${(usedBytes / MB).toFixed(1)}/${numericLimit.toFixed(numericLimit < 10 ? 1 : 0)} MB`;
    }

    // STORAGE Metric
    if (metrics.limits.storage && metrics.limits.storage !== 0) {
        const storageUsedInBytes = metrics.usages.storage || 0;
        const storageLimitInBytes = metrics.limits.storage;
        StorageText = formatUsageMetric(storageUsedInBytes, storageLimitInBytes);
        storagePercentage = (storageUsedInBytes / storageLimitInBytes) * 100;
    }

    // BW Metric
    if (metrics.limits.bandwidth && metrics.limits.bandwidth !== 0) {
        const bwUsageInBytes = metrics.usages.bandwidth || 0;
        const bwLimitInBytes = metrics.limits.bandwidth;
        BandwidthText = formatUsageMetric(bwUsageInBytes, bwLimitInBytes);
        bandwidthPercentage = (bwUsageInBytes / bwLimitInBytes) * 100;
    }

    if (metrics.limits.storage <= metrics.usages.storage || metrics.limits.bandwidth <= metrics.usages.bandwidth) {
        nudgeForPlanUpgrade = true;
    }
    const ReferralText = `${referralCount}/10`;

    const textLength = Math.max(...[StorageText, BandwidthText, ReferralText].map((text) => text.length));
    // 312 - (2 * 16) - (2 * 16) - 24 - (11 * 2) = 202
    // we have 202 px to work with
    // given we have a font size of 12px, we assume roughly 8px per character
    const textWidth = textLength * 8;
    const progressWidth = ProgressWidth.find(({width}) => width <= 202 - textWidth)?.style ?? ProgressWidth[0].style;

    return (
        <div className='flex w-full flex-col rounded-[0.5rem] bg-[#363F72] px-[1rem] py-[1.25rem]'>
            {metrics.isLoading && metrics.limits.storage <= 0 ? ( // show loader only for first metric fetch
                <div className='flex items-center justify-center'>
                    <CommonSpinLoader size={'sm'} />
                </div>
            ) : (
                <>
                    {!nudgeForPlanUpgrade ? (
                        <span className='pb-[1rem] text-[0.875rem] font-[600] text-[#FFFFFF]'>
                            You are using Pixel {tierText} plan
                        </span>
                    ) : (
                        <div className={'mb-8 text-[14px]'}>
                            <UpgradeCard
                                msg={"Oops! Looks like you're running low on credits. Upgrade to keep going!"}
                            />
                        </div>
                    )}

                    <ProgressSection
                        icon={<UploadCloud1 />}
                        progressBar={
                            <CustomProgressBar
                                value={storagePercentage}
                                variant='secondary'
                                indicator={
                                    storagePercentage >= 80
                                        ? storagePercentage > 100
                                            ? 'overflow'
                                            : 'warning'
                                        : 'primary'
                                }
                                label='Storage'
                                className={progressWidth}
                            />
                        }
                        text={StorageText}
                    />

                    <ProgressSection
                        icon={<Speedometer />}
                        progressBar={
                            <CustomProgressBar
                                value={bandwidthPercentage}
                                variant='secondary'
                                indicator={
                                    bandwidthPercentage >= 80
                                        ? bandwidthPercentage > 100
                                            ? 'overflow'
                                            : 'warning'
                                        : 'primary'
                                }
                                label='Bandwidth'
                                className={progressWidth}
                            />
                        }
                        text={BandwidthText}
                    />

                    <ProgressSection
                        icon={<ImageCheck />}
                        progressBar={
                            <CustomProgressBar
                                value={getReferredUser()}
                                variant='secondary'
                                label='Number of people referred'
                                className={progressWidth}
                            />
                        }
                        text={ReferralText}
                    />

                    {!nudgeForPlanUpgrade && (
                        <button
                            className={'pb-[1rem] text-start text-[0.875rem] font-[600] text-[#FFFFFF]'}
                            onClick={() => navigate('/settings/plans')}>
                            Upgrade Plan
                        </button>
                    )}

                    <div className='border-t-[1px] border-[#4E5BA6] pt-[1rem]'>
                        <span className='text-[0.875rem] font-[600] text-[#FFFFFF]'>
                            Refer & get additional Bandwidth
                        </span>
                        {showReferral && (
                            <div className='pt-[1rem]'>
                                <Referral code={props.referralCode} referralCount={referralCount} />
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default UserPlan;
